import axios, { type AxiosRequestConfig } from "axios";
import MY_CONFIG from "./constants";

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: Method | any;
  body?: any;
  headers?: Record<string, string>;
  query?: Record<string, string | number | any>;
  auth?: boolean;
  responseType?: "json" | "blob" | "text";
  baseUrl?: string;
}

function joinUrl(base: string, path: string) {
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

export async function makeRequest<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    method = "GET",
    body,
    headers = {},
    query,
    auth = true,
    responseType = "json",
    baseUrl = MY_CONFIG.API_URL,
  } = options;

  const isAbsoluteUrl = /^https?:\/\//i.test(endpoint);

  const config: AxiosRequestConfig = {
    url: isAbsoluteUrl ? endpoint : joinUrl(baseUrl, endpoint),
    method,
    headers: { 
      "Content-Type": "application/json",
      ...headers 
    },
    params: query,
    responseType: responseType === "text" ? "text" : "json",
  };

  if (body && !(body instanceof FormData)) {
    config.data = body;
  } else if (body instanceof FormData) {
    // For FormData, remove Content-Type header to let browser set it with boundary
    delete config.headers["Content-Type"];
    config.data = body;
  }

  // Fix: Use the same token name as in AuthContext
  if (auth && typeof window !== "undefined") {
    const token = localStorage.getItem(MY_CONFIG.accesstokenName);
    
    if (token) {
      config.headers!["Authorization"] = `Bearer ${token}`;
      console.log(`🔐 Auth header added for ${method} ${endpoint}`);
    } else {
      console.warn(`⚠️ Auth required but no token found for ${method} ${endpoint}`);
      console.warn(`Looking for key: "${MY_CONFIG.accesstokenName}" in localStorage`);
      console.warn("Available localStorage keys:", Object.keys(localStorage));
    }
  }

  // Log the request for debugging (only in development)
  if (process.env.NODE_ENV === "development") {
    console.log(`📡 ${method} ${config.url}`);
    if (query) console.log("📊 Query:", query);
    if (body && !(body instanceof FormData)) console.log("📦 Body:", body);
  }

  try {
    const res = await axios(config);
    
    if (process.env.NODE_ENV === "development") {
      console.log(`✅ Response ${method} ${endpoint}:`, res.status);
    }
    
    return res.data as T;
  } catch (err: any) {
    console.error(`❌ Request failed for ${method} ${endpoint}:`, err);
    
    // Handle 401 Unauthorized - token might be expired
    if (err.response?.status === 401 && auth) {
      console.error("Token might be expired. Consider refreshing.");
      // You could dispatch a custom event here to trigger token refresh
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    
    if (err.response?.data) {
      throw new Error(err.response.data.error || err.response.data.message || "Request failed");
    }
    throw new Error(err.message || "Request failed");
  }
}